import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Sparkles, 
  RefreshCw, 
  Save,
  Eye,
  EyeOff,
  Loader2,
  Palette,
  Type,
  Image as ImageIcon,
  Move,
  Settings2,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Smartphone,
  Monitor,
  GripVertical,
  Wand2,
  Download,
  Layout,
  MousePointer,
  Gift,
  Users,
  Star,
  Zap,
  Play,
  ExternalLink,
  HelpCircle,
  Clock,
  Video,
  Undo2,
  Pipette,
  LayoutTemplate,
  Shield,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { DesignSettingsPanel, ColorSchemeSelector } from './landing/DesignSettingsPanel';
import { COLOR_SCHEMES, TYPOGRAPHY_PRESETS, getGoogleFontsUrl } from '@/lib/landingDesignSystem';
import MetaPixelTracker from './MetaPixelTracker';

// ==================== HELPER: CHECK IF BACKGROUND IS DARK ====================
// Consistent function to determine if a section background should use light text
// Uses colorScheme + bgType to ensure proper contrast
const isBackgroundDark = (bgType, colorScheme, colors = null) => {
  // EXPLICIT LIGHT BACKGROUNDS - always need dark text
  const explicitlyLight = ['light', 'white', 'cream', 'beige'];
  if (explicitlyLight.includes(bgType)) {
    return false; // Light background = NOT dark = use dark text
  }
  
  // EXPLICIT DARK BACKGROUNDS - always need light text
  const explicitlyDark = ['dark', 'background', 'gradient', 'brand', 'primary', 'accent'];
  if (explicitlyDark.includes(bgType)) {
    return true; // Dark background = IS dark = use light text
  }
  
  // LIGHT THEMES - default to light backgrounds
  const lightThemes = ['clean', 'cream', 'minimal', 'soft', 'elegant'];
  if (lightThemes.includes(colorScheme)) {
    return bgType === 'dark' || bgType === 'brand' || bgType === 'gradient';
  }
  
  // DARK THEMES (midnight, neon, corporate, luxury, etc.)
  // These themes have dark backgrounds by default
  const darkThemes = ['midnight', 'neon', 'corporate', 'luxury', 'modern', 'bold'];
  if (darkThemes.includes(colorScheme)) {
    // Only 'light' bg in dark themes should have dark text
    return bgType !== 'light';
  }
  
  // For 'surface' background, check actual color luminance
  if (bgType === 'surface' && colors?.surface) {
    const hex = colors.surface.replace('#', '');
    if (hex.length >= 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance < 0.5; // Dark surface = light text needed
    }
  }
  
  // DEFAULT: Assume dark background (safer - white text is readable on most backgrounds)
  return true;
};

// Get text color based on background - ALWAYS HIGH CONTRAST
const getTextColor = (isDark, colors) => {
  // isDark=true means background is dark, so use WHITE text
  // isDark=false means background is light, so use DARK text
  if (isDark) {
    return '#ffffff'; // Pure white for maximum contrast on dark
  }
  return '#111827'; // Very dark gray for maximum contrast on light
};

const getTextMutedColor = (isDark, colors) => {
  if (isDark) {
    return 'rgba(255,255,255,0.8)'; // Light muted text on dark bg
  }
  return 'rgba(17,24,39,0.7)'; // Dark muted text on light bg
};

// Get heading color - USE BRAND COLOR ON LIGHT, WHITE ON DARK
const getHeadingColor = (isDark, colors, brandColors) => {
  // CRITICAL: On dark backgrounds, ALWAYS use white text for readability
  if (isDark) {
    return '#ffffff'; // Pure white headings on dark backgrounds - NO EXCEPTIONS
  }
  // On light backgrounds, use brand color if available, otherwise dark text
  return brandColors?.primary || '#111827';
};

// ==================== LIVE ROOM STATE INDICATOR ====================
// Component for showing dynamic state of a Live Room landing
// States: pre-event (countdown), live (access button), post-event (replay)
const LiveRoomStateIndicator = ({ briefData, brandColors, salaUrl, replayUrl }) => {
  const [currentState, setCurrentState] = useState('pre-event'); // pre-event, live, post-event
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Parse event date and time from brief
  const getEventDateTime = useCallback(() => {
    const eventDate = briefData?.event_date || briefData?.eventDate;
    const eventTime = briefData?.event_time || briefData?.eventTime || '18:00';
    
    if (!eventDate) return null;
    
    // Combine date and time
    const dateTimeStr = `${eventDate}T${eventTime}`;
    const eventDateTime = new Date(dateTimeStr);
    
    return isNaN(eventDateTime.getTime()) ? null : eventDateTime;
  }, [briefData]);

  // Update state and countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      const eventDateTime = getEventDateTime();
      if (!eventDateTime) {
        setCurrentState('pre-event');
        return;
      }
      
      const thirtyMinBefore = new Date(eventDateTime.getTime() - 30 * 60 * 1000);
      const threeHoursAfter = new Date(eventDateTime.getTime() + 3 * 60 * 60 * 1000);
      
      if (now < thirtyMinBefore) {
        // Pre-event: show countdown
        setCurrentState('pre-event');
        
        const diff = eventDateTime.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setCountdown({ days, hours, minutes, seconds });
      } else if (now >= thirtyMinBefore && now <= threeHoursAfter) {
        // Live: show access button
        setCurrentState('live');
      } else {
        // Post-event: show replay
        setCurrentState('post-event');
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [getEventDateTime]);

  const eventDateTime = getEventDateTime();
  const hasReplay = replayUrl && replayUrl.trim() !== '';
  
  // Format date for display
  const formatEventDate = () => {
    if (!eventDateTime) return '[AÑADIR FECHA]';
    
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return eventDateTime.toLocaleDateString('es-ES', options);
  };

  // Render countdown block
  const CountdownDisplay = () => (
    <div className="flex justify-center gap-4 my-6">
      {[
        { value: countdown.days, label: 'Días' },
        { value: countdown.hours, label: 'Horas' },
        { value: countdown.minutes, label: 'Min' },
        { value: countdown.seconds, label: 'Seg' }
      ].map((item) => (
        <div 
          key={item.label}
          className="flex flex-col items-center p-4 rounded-lg min-w-[70px]"
          style={{ backgroundColor: brandColors.primary + '20' }}
        >
          <span className="text-3xl font-bold" style={{ color: brandColors.primary }}>
            {String(item.value).padStart(2, '0')}
          </span>
          <span className="text-xs text-gray-500 uppercase">{item.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6 rounded-xl border-2 border-dashed" style={{ borderColor: brandColors.primary + '40' }}>
      {/* State indicator badge */}
      <div className="flex justify-center mb-4">
        <span 
          className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
            currentState === 'live' 
              ? 'bg-red-500 text-white animate-pulse' 
              : currentState === 'post-event'
              ? 'bg-green-500 text-white'
              : 'bg-amber-500 text-white'
          }`}
        >
          {currentState === 'live' ? '🔴 EN DIRECTO' : currentState === 'post-event' ? '📺 REPLAY DISPONIBLE' : '⏳ PRÓXIMAMENTE'}
        </span>
      </div>
      
      {/* PRE-EVENT STATE */}
      {currentState === 'pre-event' && (
        <div className="text-center">
          <h3 className="text-xl font-bold mb-2">El directo comienza en:</h3>
          {eventDateTime ? (
            <CountdownDisplay />
          ) : (
            <div className="p-4 bg-amber-500/10 rounded-lg my-4">
              <span className="text-amber-600 font-semibold">[AÑADIR FECHA Y HORA DEL EVENTO]</span>
            </div>
          )}
          <p className="text-gray-600 mt-4">
            📅 {formatEventDate()}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            La sala se abrirá 30 minutos antes del evento
          </p>
        </div>
      )}
      
      {/* LIVE STATE */}
      {currentState === 'live' && (
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-lg font-bold text-red-600">¡ESTAMOS EN DIRECTO!</span>
          </div>
          
          {salaUrl ? (
            <a 
              href={salaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-transform hover:scale-105"
              style={{ backgroundColor: brandColors.primary }}
            >
              <Play className="w-5 h-5" />
              ENTRAR AL DIRECTO
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <div className="p-4 bg-amber-500/10 rounded-lg">
              <span className="text-amber-600 font-semibold">[AÑADIR URL DE SALA]</span>
            </div>
          )}
          
          <p className="text-sm text-gray-500 mt-4">
            Haz clic en el botón para unirte ahora
          </p>
        </div>
      )}
      
      {/* POST-EVENT STATE */}
      {currentState === 'post-event' && (
        <div className="text-center">
          <h3 className="text-xl font-bold mb-4">
            {hasReplay ? '¡Ya puedes ver el replay!' : 'El directo ha finalizado'}
          </h3>
          
          {hasReplay ? (
            <>
              {/* Video embed placeholder */}
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4">
                {replayUrl.includes('youtube.com') || replayUrl.includes('youtu.be') ? (
                  <iframe
                    src={replayUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : replayUrl.includes('vimeo.com') ? (
                  <iframe
                    src={replayUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <a 
                    href={replayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center h-full text-white hover:text-gray-300"
                  >
                    <div className="text-center">
                      <Play className="w-16 h-16 mx-auto mb-2" />
                      <span>Ver replay</span>
                    </div>
                  </a>
                )}
              </div>
              <a 
                href={replayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold"
                style={{ backgroundColor: brandColors.primary }}
              >
                <Play className="w-4 h-4" />
                Ver Replay Completo
              </a>
            </>
          ) : (
            <div className="p-6 bg-gray-100 rounded-lg">
              <p className="text-gray-600 mb-2">El replay estará disponible pronto.</p>
              <p className="text-sm text-gray-500">[AÑADIR URL DE REPLAY]</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MARKDOWN PARSER ====================
// Simple markdown parser for block text content
const parseMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  return text
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>') // ***bold italic***
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // **bold**
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // *italic*
    .replace(/__(.*?)__/g, '<strong>$1</strong>') // __bold__
    .replace(/_(.*?)_/g, '<em>$1</em>') // _italic_
    .replace(/\n/g, '<br/>'); // newlines
};

// Component for rendering markdown text safely
const MarkdownText = ({ children, className = '' }) => {
  if (!children) return null;
  return (
    <span 
      className={className}
      dangerouslySetInnerHTML={{ __html: parseMarkdown(children) }}
    />
  );
};

// ==================== BLOCK TEMPLATES ====================
const BLOCK_TEMPLATES = {
  hero: {
    type: 'hero',
    label: 'Hero Section',
    icon: MousePointer,
    defaultContent: {
      preheadline: 'Para [avatar] que quieren [deseo]',
      headline: 'Titular Principal Impactante',
      subheadline: 'Subtítulo que refuerza la promesa y genera curiosidad',
      cta: 'QUIERO ACCEDER AHORA',
      ctaSubtext: '100% garantizado o te devolvemos tu dinero'
    },
    defaultStyles: {
      background: 'gradient',
      showExpertPhoto: true,
      textAlign: 'center',
      padding: 'large'
    }
  },
  benefits: {
    type: 'benefits',
    label: 'Beneficios',
    icon: Gift,
    defaultContent: {
      title: 'Lo que vas a conseguir',
      items: [
        { icon: 'check', title: 'Beneficio 1', description: 'Descripción del primer beneficio' },
        { icon: 'check', title: 'Beneficio 2', description: 'Descripción del segundo beneficio' },
        { icon: 'check', title: 'Beneficio 3', description: 'Descripción del tercer beneficio' }
      ]
    },
    defaultStyles: {
      background: 'light',
      columns: 3,
      iconStyle: 'circle'
    }
  },
  testimonials: {
    type: 'testimonials',
    label: 'Testimonios',
    icon: Users,
    defaultContent: {
      title: 'Lo que dicen nuestros clientes',
      items: [
        { name: 'María García', role: 'Emprendedora', text: 'Increíble transformación...', avatar: null },
        { name: 'Carlos López', role: 'CEO', text: 'Los resultados hablan por sí solos...', avatar: null }
      ]
    },
    defaultStyles: {
      background: 'dark',
      layout: 'grid'
    }
  },
  features: {
    type: 'features',
    label: 'Características',
    icon: Star,
    defaultContent: {
      title: '¿Qué incluye?',
      items: [
        { icon: 'zap', title: 'Característica 1', value: 'Valor incluido' },
        { icon: 'zap', title: 'Característica 2', value: 'Valor incluido' },
        { icon: 'zap', title: 'Característica 3', value: 'Valor incluido' }
      ]
    },
    defaultStyles: {
      background: 'light',
      showIcons: true
    }
  },
  cta: {
    type: 'cta',
    label: 'Call to Action',
    icon: Zap,
    defaultContent: {
      headline: '¿Listo para empezar?',
      subheadline: 'Únete a los cientos de personas que ya han transformado su vida',
      cta: 'COMENZAR AHORA',
      urgency: 'Solo quedan 10 plazas disponibles'
    },
    defaultStyles: {
      background: 'gradient',
      size: 'large'
    }
  },
  video: {
    type: 'video',
    label: 'Video',
    icon: Play,
    defaultContent: {
      title: 'Mira este video',
      videoUrl: '',
      thumbnail: null
    },
    defaultStyles: {
      background: 'dark',
      aspectRatio: '16:9'
    }
  },
  faq: {
    type: 'faq',
    label: 'Preguntas Frecuentes',
    icon: HelpCircle,
    defaultContent: {
      title: 'Preguntas Frecuentes',
      items: [
        { question: '¿Para quién es esto?', answer: 'Para profesionales que buscan resultados.' },
        { question: '¿Qué garantía tengo?', answer: 'Garantía de satisfacción incluida.' },
        { question: '¿Cuánto tiempo tendré acceso?', answer: 'Acceso completo desde el primer día.' },
        { question: '¿Hay soporte?', answer: 'Sí, soporte incluido durante todo el proceso.' }
      ]
    },
    defaultStyles: {
      background: 'light',
      layout: 'accordion'
    }
  },
  live_room: {
    type: 'live_room',
    label: 'Sala del Directo',
    icon: Video,
    defaultContent: {
      title: 'Sala del Directo',
      salaUrl: '',
      replayUrl: ''
    },
    defaultStyles: {
      background: 'light'
    }
  }
};

// ==================== STYLE EDITOR PANEL ====================
const StyleEditorPanel = ({ block, brandColors, onUpdateStyles, onClose }) => {
  const [styles, setStyles] = useState(block.styles || {});
  const fileInputRef = useRef(null);
  
  const backgroundOptions = [
    { id: 'transparent', label: 'Transparente', preview: 'bg-transparent border border-dashed' },
    { id: 'light', label: 'Claro', preview: 'bg-white' },
    { id: 'dark', label: 'Oscuro', preview: 'bg-gray-900' },
    { id: 'gradient', label: 'Degradado', preview: 'bg-gradient-to-br from-brand to-purple-600' },
    { id: 'brand', label: 'Marca', preview: '', style: { backgroundColor: brandColors.primary } },
    { id: 'custom_image', label: 'Imagen', preview: 'bg-cover', isUpload: true }
  ];

  const handleChange = (key, value) => {
    const newStyles = { ...styles, [key]: value };
    setStyles(newStyles);
    onUpdateStyles(newStyles);
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagen demasiado grande, máximo 5MB');
      return;
    }
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato no válido. Usa JPG, PNG o WebP');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const newStyles = { 
        ...styles, 
        background: 'custom_image',
        backgroundImage: reader.result,
        backgroundOverlay: styles.backgroundOverlay || false
      };
      setStyles(newStyles);
      onUpdateStyles(newStyles);
      toast.success('Imagen de fondo aplicada');
    };
    reader.readAsDataURL(file);
  };
  
  const handleBackgroundClick = (optionId, isUpload) => {
    if (isUpload) {
      fileInputRef.current?.click();
    } else {
      handleChange('background', optionId);
      // Clear image when switching to non-image background
      if (styles.backgroundImage) {
        const newStyles = { ...styles, background: optionId, backgroundImage: null };
        setStyles(newStyles);
        onUpdateStyles(newStyles);
      }
    }
  };

  return (
    <div className="absolute top-0 right-0 w-72 bg-surface border border-border-subtle rounded-xl shadow-2xl z-50 overflow-hidden">
      <div className="px-4 py-3 bg-app-bg/50 border-b border-border-subtle flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-brand-500" />
          <span className="font-medium text-text-main text-sm">Editar Diseño</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-surface rounded-md">
          <X className="w-4 h-4 text-text-muted" />
        </button>
      </div>
      
      <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />
        
        {/* Background */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider block mb-2">Fondo</label>
          <div className="grid grid-cols-3 gap-2">
            {backgroundOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleBackgroundClick(opt.id, opt.isUpload)}
                className={`p-2 rounded-lg border-2 transition-all ${
                  styles.background === opt.id 
                    ? 'border-brand-500 ring-2 ring-brand/20' 
                    : 'border-border-subtle hover:border-brand/50'
                }`}
              >
                <div 
                  className={`w-full h-8 rounded ${opt.preview} ${opt.isUpload ? 'flex items-center justify-center bg-gray-100' : ''}`}
                  style={opt.isUpload && styles.backgroundImage ? { backgroundImage: `url(${styles.backgroundImage})`, backgroundSize: 'cover' } : opt.style}
                >
                  {opt.isUpload && !styles.backgroundImage && <ImageIcon className="w-4 h-4 text-gray-400" />}
                </div>
                <span className="text-[10px] text-text-muted mt-1 block">{opt.label}</span>
              </button>
            ))}
          </div>
          
          {/* Overlay toggle for custom image */}
          {styles.background === 'custom_image' && styles.backgroundImage && (
            <button
              onClick={() => handleChange('backgroundOverlay', !styles.backgroundOverlay)}
              className={`mt-3 w-full p-2 rounded-lg border-2 flex items-center justify-between text-sm ${
                styles.backgroundOverlay ? 'border-brand-500 bg-brand/10' : 'border-border-subtle'
              }`}
            >
              <span className="text-text-main">Oscurecer fondo</span>
              {styles.backgroundOverlay && <Check className="w-4 h-4 text-brand-500" />}
            </button>
          )}
        </div>

        {/* Text Alignment */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider block mb-2">Alineación</label>
          <div className="flex bg-app-bg rounded-lg p-1">
            {[
              { id: 'left', icon: AlignLeft },
              { id: 'center', icon: AlignCenter },
              { id: 'right', icon: AlignRight }
            ].map((align) => (
              <button
                key={align.id}
                onClick={() => handleChange('textAlign', align.id)}
                className={`flex-1 p-2 rounded-md transition-all ${
                  styles.textAlign === align.id ? 'bg-brand-500 text-white' : 'text-text-muted hover:text-text-main'
                }`}
              >
                <align.icon className="w-4 h-4 mx-auto" />
              </button>
            ))}
          </div>
        </div>

        {/* Padding */}
        <div>
          <label className="text-xs text-text-muted uppercase tracking-wider block mb-2">Espaciado</label>
          <div className="flex bg-app-bg rounded-lg p-1">
            {['small', 'medium', 'large'].map((size) => (
              <button
                key={size}
                onClick={() => handleChange('padding', size)}
                className={`flex-1 p-2 rounded-md text-xs transition-all ${
                  styles.padding === size ? 'bg-brand-500 text-white' : 'text-text-muted hover:text-text-main'
                }`}
              >
                {size === 'small' ? 'S' : size === 'medium' ? 'M' : 'L'}
              </button>
            ))}
          </div>
        </div>

        {/* Show Expert Photo (for hero) */}
        {block.type === 'hero' && (
          <div>
            <label className="text-xs text-text-muted uppercase tracking-wider block mb-2">Foto Experto</label>
            <button
              onClick={() => handleChange('showExpertPhoto', !styles.showExpertPhoto)}
              className={`w-full p-3 rounded-lg border-2 flex items-center justify-between ${
                styles.showExpertPhoto ? 'border-brand-500 bg-brand/10' : 'border-border-subtle'
              }`}
            >
              <span className="text-sm text-text-main">Mostrar foto</span>
              {styles.showExpertPhoto && <Check className="w-4 h-4 text-brand-500" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== EDITABLE TEXT COMPONENT ====================
const EditableText = ({ value, onChange, className, style, as: Component = 'p', placeholder, renderMarkdown = true }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    if (text !== value) {
      onChange(text);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setText(value);
      setIsEditing(false);
    }
  };

  // Determine if we need light or dark background for editing based on text color
  const textColorValue = style?.color || '#ffffff';
  const isLightText = textColorValue === '#ffffff' || textColorValue === 'white' || textColorValue?.toLowerCase()?.includes('fff');
  const editBgClass = isLightText ? 'bg-white/20' : 'bg-black/10';
  const hoverBgClass = isLightText ? 'hover:bg-white/10' : 'hover:bg-black/5';

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`${className} ${editBgClass} border-b-2 border-brand-500 outline-none w-full rounded px-1`}
        style={{ ...style, backgroundColor: 'transparent' }}
        placeholder={placeholder}
      />
    );
  }

  // Render with markdown parsing when not editing
  const displayContent = value 
    ? (renderMarkdown ? <MarkdownText>{value}</MarkdownText> : value)
    : <span className="opacity-50">{placeholder}</span>;

  return (
    <Component 
      className={`${className} cursor-text ${hoverBgClass} rounded px-1 -mx-1 transition-colors`}
      style={style}
      onClick={() => setIsEditing(true)}
    >
      {displayContent}
    </Component>
  );
};

// ==================== HERO BLOCK RENDERER ====================
// ==================== HERO BLOCK RENDERER (PREMIUM) ====================
const HeroBlockRenderer = ({ block, brandColors, colorScheme, expertPhoto, onUpdateContent, viewMode = 'desktop' }) => {
  const content = block.content || BLOCK_TEMPLATES.hero.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.hero.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  const isMobile = viewMode === 'mobile';
  
  // Determine if background is dark to set correct text colors
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const mutedTextColor = getTextMutedColor(isDark, colors);
  
  const getBackgroundStyle = () => {
    switch (styles.background) {
      case 'gradient':
        return { 
          background: colors.heroGradient || `linear-gradient(180deg, ${colors.background} 0%, ${colors.surface} 100%)`
        };
      case 'brand':
        return { background: colors.gradient };
      case 'secondary':
        return { backgroundColor: colors.secondary };
      case 'dark':
        return { backgroundColor: colors.background };
      case 'light':
        return { backgroundColor: colors.surface };
      default:
        return { background: colors.heroGradient };
    }
  };

  const getPadding = () => {
    // Responsive padding for mobile
    const basePadding = isMobile ? 'py-12 px-4' : 'py-24 px-6';
    switch (styles.padding) {
      case 'small': return isMobile ? 'py-8 px-4' : 'py-16 px-6';
      case 'large': return isMobile ? 'py-16 px-4' : 'py-32 px-8';
      default: return basePadding;
    }
  };

  const getTextAlign = () => {
    switch (styles.textAlign) {
      case 'left': return 'text-left items-start';
      case 'right': return 'text-right items-end';
      default: return 'text-center items-center';
    }
  };

  return (
    <div 
      className={`${getPadding()} relative overflow-hidden ${isMobile ? 'min-h-[60vh]' : 'min-h-[80vh]'} flex items-center`}
      style={getBackgroundStyle()}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-1/4 -right-1/4 w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
          style={{ background: colors.gradient }}
        />
        <div 
          className="absolute -bottom-1/4 -left-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-15"
          style={{ background: colors.gradient }}
        />
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>
      
      <div className={`relative z-10 max-w-5xl mx-auto w-full flex flex-col ${getTextAlign()}`}>
        {/* Pre-headline Badge */}
        {content.preheadline && (
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 mx-auto"
            style={{ 
              backgroundColor: `${colors.primary}20`,
              color: colors.primary,
              border: `1px solid ${colors.primary}40`
            }}
          >
            <Zap className="w-4 h-4" />
            <EditableText
              value={content.preheadline}
              onChange={(val) => onUpdateContent({ ...content, preheadline: val })}
              className="inline"
              placeholder="Pre-titular..."
            />
          </div>
        )}
        
        {/* Main Headline - ALWAYS uses contrast-safe color */}
        <EditableText
          value={content.headline}
          onChange={(val) => onUpdateContent({ ...content, headline: val })}
          className={`${isMobile ? 'text-2xl' : 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl'} font-extrabold mb-6 leading-[1.1] tracking-tight`}
          style={{ color: textColor }}
          as="h1"
          placeholder="Titular principal..."
        />
        
        {/* Subheadline - uses muted text color for contrast */}
        <EditableText
          value={content.subheadline}
          onChange={(val) => onUpdateContent({ ...content, subheadline: val })}
          className={`${isMobile ? 'text-base' : 'text-xl md:text-2xl'} mb-10 max-w-3xl leading-relaxed`}
          style={{ color: mutedTextColor }}
          placeholder="Subtítulo..."
        />
        
        {/* Expert Photo */}
        {styles.showExpertPhoto && expertPhoto && (
          <div className="mb-8">
            <div className="relative inline-block">
              <img 
                src={expertPhoto}
                alt="Experto"
                className="w-28 h-28 rounded-full object-cover shadow-2xl"
                style={{ border: `4px solid ${colors.primary}40` }}
              />
              <div 
                className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: colors.gradient }}
              >
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        )}
        
        {/* CTA Button */}
        <button 
          className="group px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto"
          style={{ 
            background: colors.gradient,
            color: '#FFFFFF',
            boxShadow: `0 10px 40px -10px ${colors.primary}80`
          }}
        >
          <EditableText
            value={content.cta}
            onChange={(val) => onUpdateContent({ ...content, cta: val })}
            className="inline"
            placeholder="Texto del botón..."
          />
          <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </button>
        
        {/* CTA Subtext */}
        {content.ctaSubtext && (
          <EditableText
            value={content.ctaSubtext}
            onChange={(val) => onUpdateContent({ ...content, ctaSubtext: val })}
            className="text-sm mt-4"
            style={{ color: mutedTextColor }}
            placeholder="Texto bajo el botón..."
          />
        )}
        
        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10" style={{ color: mutedTextColor }}>
          <div className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4" style={{ color: colors.primary }} />
            <span>Garantía 30 días</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4" style={{ color: colors.primary }} />
            <span>+1,000 clientes</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Award className="w-4 h-4" style={{ color: colors.primary }} />
            <span>Soporte incluido</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== BENEFITS BLOCK RENDERER (PREMIUM) ====================
const BenefitsBlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent, briefData, api, viewMode = 'desktop' }) => {
  const content = block.content || BLOCK_TEMPLATES.benefits.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.benefits.defaultStyles;
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const textMutedColor = getTextMutedColor(isDark, colors);
  
  // Force single column in mobile view
  const isMobile = viewMode === 'mobile';
  
  const getBackgroundStyle = () => {
    if (styles.background === 'custom_image' && styles.backgroundImage) {
      return { 
        backgroundImage: `url(${styles.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      };
    }
    switch (styles.background) {
      case 'dark':
        return { backgroundColor: colors.background };
      case 'gradient':
        return { background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.secondary}15 100%)` };
      case 'brand':
        return { backgroundColor: `${colors.primary}10` };
      case 'light':
        return { backgroundColor: colors.surface };
      default:
        return { backgroundColor: colors.surface };
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...content.items];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdateContent({ ...content, items: newItems });
  };
  
  const regenerateWithAI = async () => {
    if (!api || !briefData) {
      toast.error('No hay datos del brief disponibles');
      return;
    }
    
    setIsRegenerating(true);
    try {
      const prompt = `Genera 3 beneficios concretos para este producto/servicio:
Producto: ${briefData.productName || briefData.product_name || 'producto'}
Público: ${briefData.targetAudience || briefData.target_audience || 'profesionales'}
Problema que resuelve: ${briefData.painPoints || briefData.pain_points || 'problemas del cliente'}
Resultado prometido: ${briefData.mainPromise || briefData.main_promise || briefData.promised_result || 'resultados'}

Devuelve JSON con este formato exacto:
{
  "beneficios": [
    { "titulo": "Beneficio específico 1", "descripcion": "Explicación de cómo ayuda al cliente" },
    { "titulo": "Beneficio específico 2", "descripcion": "Explicación de cómo ayuda al cliente" },
    { "titulo": "Beneficio específico 3", "descripcion": "Explicación de cómo ayuda al cliente" }
  ]
}`;

      const response = await api.post('/ai/openrouter/generate', {
        prompt,
        system_prompt: 'Eres un experto en copywriting. Responde SOLO con JSON válido, sin explicaciones.',
        max_tokens: 500
      });
      
      if (response.data?.content) {
        // Parse JSON from response
        const jsonMatch = response.data.content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const data = JSON.parse(jsonMatch[0]);
          if (data.beneficios && Array.isArray(data.beneficios)) {
            const newItems = data.beneficios.map(b => ({
              icon: 'check',
              title: b.titulo,
              description: b.descripcion
            }));
            onUpdateContent({ ...content, items: newItems });
            toast.success('Beneficios regenerados con IA');
          }
        }
      }
    } catch (error) {
      
      toast.error('Error al regenerar. Intenta de nuevo.');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="py-24 px-6 relative" style={getBackgroundStyle()}>
      {/* Overlay for custom image */}
      {styles.background === 'custom_image' && styles.backgroundOverlay && (
        <div className="absolute inset-0 bg-black/45" />
      )}
      
      {/* Background accent */}
      <div 
        className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none"
        style={{ background: colors.gradient }}
      />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <EditableText
              value={content.title}
              onChange={(val) => onUpdateContent({ ...content, title: val })}
              className="text-3xl md:text-5xl font-bold"
              style={{ color: textColor }}
              as="h2"
              placeholder="Título de la sección..."
            />
            <button
              onClick={regenerateWithAI}
              disabled={isRegenerating}
              className="p-2 rounded-lg transition-colors flex items-center gap-1"
              style={{ 
                backgroundColor: `${colors.primary}20`,
                color: colors.primary
              }}
              title="Regenerar con IA"
            >
              {isRegenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </button>
          </div>
          {content.subtitle && (
            <p className="text-xl max-w-2xl mx-auto" style={{ color: textMutedColor }}>
              {content.subtitle}
            </p>
          )}
        </div>
        
        {/* Benefits Grid - Using explicit grid classes for Tailwind compatibility */}
        <div className={`grid gap-8 ${
          isMobile ? 'grid-cols-1' :
          (styles.columns || 3) === 1 ? 'grid-cols-1' :
          (styles.columns || 3) === 2 ? 'md:grid-cols-2' :
          'md:grid-cols-3'
        }`}>
          {content.items.map((item, idx) => (
            <div 
              key={`benefit-${item.title?.slice(0,10) || idx}`}
              className="group p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2"
              style={{ 
                backgroundColor: isDark ? colors.surface : '#FFFFFF',
                border: `1px solid ${isDark ? colors.text : '#1a1a2e'}10`,
                boxShadow: '0 4px 24px -8px rgba(0,0,0,0.15)'
              }}
            >
              {/* Icon */}
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                style={{ background: colors.gradient }}
              >
                <Check className="w-7 h-7 text-white" />
              </div>
              
              {/* Content */}
              <EditableText
                value={item.title}
                onChange={(val) => updateItem(idx, 'title', val)}
                className="text-xl font-bold mb-3"
                style={{ color: textColor }}
                as="h3"
                placeholder="Título beneficio..."
              />
              <EditableText
                value={item.description}
                onChange={(val) => updateItem(idx, 'description', val)}
                style={{ color: textMutedColor }}
                placeholder="Descripción..."
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== CTA BLOCK RENDERER ====================
// ==================== CTA BLOCK RENDERER (PREMIUM) ====================
const CTABlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent }) => {
  const content = block.content || BLOCK_TEMPLATES.cta.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.cta.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);

  return (
    <div 
      className="py-24 px-6 relative overflow-hidden"
      style={{ background: colors.gradient }}
    >
      {/* Animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ backgroundColor: colors.secondary }}
        />
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ backgroundColor: colors.accent }}
        />
      </div>
      
      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Urgency Badge */}
        {content.urgency && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium mb-6">
            <Clock className="w-4 h-4" />
            <EditableText
              value={content.urgency}
              onChange={(val) => onUpdateContent({ ...content, urgency: val })}
              className="inline"
              placeholder="Texto urgencia..."
            />
          </div>
        )}
        
        <EditableText
          value={content.headline}
          onChange={(val) => onUpdateContent({ ...content, headline: val })}
          className="text-4xl md:text-6xl font-extrabold text-white mb-6"
          as="h2"
          placeholder="Titular CTA..."
        />
        <EditableText
          value={content.subheadline}
          onChange={(val) => onUpdateContent({ ...content, subheadline: val })}
          className="text-xl text-white/80 mb-10 max-w-2xl mx-auto"
          placeholder="Subtítulo..."
        />
        
        <button 
          className="px-12 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:scale-105"
          style={{ 
            backgroundColor: '#FFFFFF',
            color: colors.primary,
            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)'
          }}
        >
          <EditableText
            value={content.cta}
            onChange={(val) => onUpdateContent({ ...content, cta: val })}
            className="inline"
            placeholder="Texto botón..."
          />
        </button>
        
        {/* Guarantee text */}
        <p className="text-white/60 text-sm mt-6">
          Garantía de devolución de 30 días. Sin preguntas.
        </p>
      </div>
    </div>
  );
};

// ==================== TESTIMONIALS BLOCK RENDERER (PREMIUM) ====================
const TestimonialsBlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent, viewMode = 'desktop' }) => {
  const content = block.content || BLOCK_TEMPLATES.testimonials.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.testimonials.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const textMutedColor = getTextMutedColor(isDark, colors);
  
  // Force single column in mobile view
  const isMobile = viewMode === 'mobile';

  const updateItem = (index, field, value) => {
    const newItems = [...content.items];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdateContent({ ...content, items: newItems });
  };

  return (
    <div 
      className="py-24 px-6"
      style={{ backgroundColor: isDark ? colors.background : colors.surface }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <EditableText
            value={content.title}
            onChange={(val) => onUpdateContent({ ...content, title: val })}
            className="text-3xl md:text-5xl font-bold mb-4"
            style={{ color: textColor }}
            as="h2"
            placeholder="Título testimonios..."
          />
        </div>
        
        {/* Testimonials Grid */}
        <div className={`grid gap-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
          {content.items.map((item, idx) => (
            <div 
              key={`testimonial-${item.name?.slice(0,10) || idx}`}
              className="p-8 rounded-3xl relative"
              style={{ 
                backgroundColor: isDark ? colors.surface : '#FFFFFF',
                border: `1px solid ${colors.text}10`
              }}
            >
              {/* Quote icon */}
              <div 
                className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center opacity-20"
                style={{ backgroundColor: colors.primary }}
              >
                <span className="text-2xl text-white">&ldquo;</span>
              </div>
              
              {/* Rating Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={`star-${i}`} 
                    className="w-5 h-5 fill-current"
                    style={{ color: colors.accent }}
                  />
                ))}
              </div>
              
              {/* Quote Text */}
              <EditableText
                value={item.text}
                onChange={(val) => updateItem(idx, 'text', val)}
                className="text-lg mb-6 leading-relaxed"
                style={{ color: textColor }}
                placeholder="Testimonio..."
              />
              
              {/* Author */}
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white"
                  style={{ background: colors.gradient }}
                >
                  {item.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <EditableText
                    value={item.name}
                    onChange={(val) => updateItem(idx, 'name', val)}
                    className="font-semibold"
                    style={{ color: textColor }}
                    placeholder="Nombre..."
                  />
                  <EditableText
                    value={item.role}
                    onChange={(val) => updateItem(idx, 'role', val)}
                    className="text-sm"
                    style={{ color: textMutedColor }}
                    placeholder="Cargo..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== FEATURES BLOCK RENDERER ====================
const FeaturesBlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent, viewMode = 'desktop' }) => {
  const content = block.content || BLOCK_TEMPLATES.features.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.features.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const textMutedColor = getTextMutedColor(isDark, colors);
  
  // Force single column in mobile view
  const isMobile = viewMode === 'mobile';

  const getBackgroundStyle = () => {
    switch (styles.background) {
      case 'dark':
        return { backgroundColor: colors.background };
      case 'gradient':
        return { background: colors.heroGradient };
      case 'brand':
        return { backgroundColor: `${colors.primary}15` };
      default:
        return { backgroundColor: colors.surface };
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...content.items];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdateContent({ ...content, items: newItems });
  };

  return (
    <div className="py-20 px-6" style={getBackgroundStyle()}>
      <div className="max-w-5xl mx-auto">
        <EditableText
          value={content.title}
          onChange={(val) => onUpdateContent({ ...content, title: val })}
          className="text-3xl md:text-4xl font-bold text-center mb-14"
          style={{ color: textColor }}
          as="h2"
          placeholder="Título de la sección..."
        />
        
        <div className={`grid gap-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'}`}>
          {content.items.map((item, idx) => (
            <div 
              key={`feature-${item.title?.slice(0,10) || idx}`}
              className="p-8 rounded-2xl transition-all hover:scale-[1.02] hover:shadow-xl"
              style={{ 
                backgroundColor: isDark ? colors.surface : '#FFFFFF',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                style={{ background: colors.gradient }}
              >
                <Zap className="w-7 h-7 text-white" />
              </div>
              <EditableText
                value={item.title}
                onChange={(val) => updateItem(idx, 'title', val)}
                className="text-xl font-bold mb-3"
                style={{ color: textColor }}
                as="h3"
                placeholder="Título característica..."
              />
              <EditableText
                value={item.value}
                onChange={(val) => updateItem(idx, 'value', val)}
                className="text-base leading-relaxed"
                style={{ color: textMutedColor }}
                placeholder="Valor o descripción..."
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== VIDEO BLOCK RENDERER ====================
const VideoBlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent }) => {
  const content = block.content || BLOCK_TEMPLATES.video.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.video.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const textMutedColor = getTextMutedColor(isDark, colors);

  const getBackgroundStyle = () => {
    switch (styles.background) {
      case 'dark':
        return { backgroundColor: colors.background };
      case 'gradient':
        return { background: colors.heroGradient };
      default:
        return { backgroundColor: colors.surface };
    }
  };

  return (
    <div 
      className="py-20 px-6"
      style={getBackgroundStyle()}
    >
      <div className="max-w-4xl mx-auto">
        <EditableText
          value={content.title}
          onChange={(val) => onUpdateContent({ ...content, title: val })}
          className="text-3xl md:text-4xl font-bold text-center mb-10"
          style={{ color: textColor }}
          as="h2"
          placeholder="Título del video..."
        />
        
        <div 
          className="relative aspect-video rounded-2xl overflow-hidden"
          style={{ 
            boxShadow: `0 25px 50px -12px ${colors.primary}30`,
            border: `1px solid ${colors.text}10`
          }}
        >
          {content.videoUrl ? (
            <video 
              src={content.videoUrl} 
              controls 
              className="w-full h-full object-cover"
            />
          ) : (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.surface} 100%)` }}
            >
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center mb-5 cursor-pointer hover:scale-110 transition-all duration-300"
                style={{ background: colors.gradient, boxShadow: `0 10px 40px ${colors.primary}50` }}
              >
                <Play className="w-12 h-12 text-white ml-1" />
              </div>
              <p style={{ color: colors.textMuted }} className="text-base">Haz clic para añadir un video</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== FAQ BLOCK RENDERER ====================
const FAQBlockRenderer = ({ block, brandColors, colorScheme, onUpdateContent, briefData, api }) => {
  const content = block.content || BLOCK_TEMPLATES.faq.defaultContent;
  const styles = block.styles || BLOCK_TEMPLATES.faq.defaultStyles;
  // Support both string ID and direct color object
  const colors = typeof colorScheme === 'object' ? colorScheme : (COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.midnight);
  const [openIndex, setOpenIndex] = useState(-1); // All closed by default
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  const isDark = isBackgroundDark(styles.background, typeof colorScheme === 'string' ? colorScheme : 'midnight', colors);
  const textColor = getTextColor(isDark, colors);
  const textMutedColor = getTextMutedColor(isDark, colors);
  
  const getBackgroundStyle = () => {
    // Custom image background
    if (styles.background === 'custom_image' && styles.backgroundImage) {
      return { 
        backgroundImage: `url(${styles.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      };
    }
    switch (styles.background) {
      case 'dark':
        return { backgroundColor: colors.background };
      case 'gradient':
        return { background: colors.heroGradient };
      case 'brand':
        return { backgroundColor: `${colors.primary}10` };
      default:
        return { backgroundColor: colors.surface };
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...(content.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdateContent({ ...content, items: newItems });
  };

  const addItem = () => {
    const newItems = [...(content.items || [])];
    newItems.push({ question: 'Nueva pregunta', answer: 'Respuesta aquí...' });
    onUpdateContent({ ...content, items: newItems });
  };
  
  const regenerateWithAI = async () => {
    if (!api || !briefData) {
      toast.error('No hay datos del brief disponibles');
      return;
    }
    
    setIsRegenerating(true);
    try {
      const prompt = `Genera 4 preguntas frecuentes y sus respuestas basadas en estas objeciones reales del cliente:
Objeciones: ${briefData.mainObjections || briefData.main_objections || 'dudas sobre el producto'}
Producto: ${briefData.productName || briefData.product_name || 'producto'}
Público objetivo: ${briefData.targetAudience || briefData.target_audience || 'clientes'}
Oferta: ${briefData.offerDetails || briefData.offer_details || 'oferta especial'}

Devuelve JSON con este formato exacto:
{
  "faqs": [
    { "question": "¿Pregunta 1?", "answer": "Respuesta completa 1" },
    { "question": "¿Pregunta 2?", "answer": "Respuesta completa 2" },
    { "question": "¿Pregunta 3?", "answer": "Respuesta completa 3" },
    { "question": "¿Pregunta 4?", "answer": "Respuesta completa 4" }
  ]
}`;

      const response = await api.post('/ai/openrouter/generate', {
        prompt,
        system_prompt: 'Eres un experto en copywriting. Responde SOLO con JSON válido, sin explicaciones.',
        max_tokens: 800
      });
      
      if (response.data?.content) {
        // Parse JSON from response
        const jsonMatch = response.data.content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const data = JSON.parse(jsonMatch[0]);
          if (data.faqs && Array.isArray(data.faqs)) {
            onUpdateContent({ ...content, items: data.faqs });
            toast.success('FAQs regeneradas con IA');
          }
        }
      }
    } catch (error) {
      
      toast.error('Error al regenerar. Intenta de nuevo.');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="py-20 px-6 relative" style={getBackgroundStyle()}>
      {/* Overlay for custom image */}
      {styles.background === 'custom_image' && styles.backgroundOverlay && (
        <div className="absolute inset-0 bg-black/45" />
      )}
      
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="flex items-center justify-center gap-4 mb-14">
          <EditableText
            value={content.title}
            onChange={(val) => onUpdateContent({ ...content, title: val })}
            className="text-3xl md:text-4xl font-bold text-center"
            style={{ color: textColor }}
            as="h2"
            placeholder="Preguntas Frecuentes..."
          />
          <button
            onClick={regenerateWithAI}
            disabled={isRegenerating}
            className="p-2.5 rounded-xl transition-all hover:scale-110"
            style={{ 
              backgroundColor: `${colors.primary}20`,
              color: colors.primary
            }}
            title="Regenerar con IA"
          >
            {isRegenerating ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
          </button>
        </div>
        
        <div className="space-y-4">
          {(content.items || []).map((item, idx) => {
            // FAQ cards are always light background, so text must be dark
            const faqCardTextColor = '#1f2937';  // Always dark text for readability
            const faqCardMutedColor = '#4b5563'; // Muted dark color
            
            return (
            <div 
              key={`faq-${item.question?.slice(0,15) || idx}`}
              className="rounded-2xl overflow-hidden transition-all"
              style={{ 
                backgroundColor: isDark ? colors.surface : '#FFFFFF',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                boxShadow: isDark ? 'none' : '0 4px 15px rgba(0,0,0,0.05)'
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-all"
                style={{ 
                  backgroundColor: openIndex === idx ? `${colors.primary}08` : 'transparent'
                }}
              >
                <span className="font-semibold pr-4 text-lg" style={{ color: faqCardTextColor }}>
                  {item.question || 'Pregunta...'}
                </span>
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                  style={{ 
                    backgroundColor: openIndex === idx ? colors.primary : `${colors.primary}20`,
                  }}
                >
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform duration-300 ${
                      openIndex === idx ? 'rotate-180' : ''
                    }`}
                    style={{ color: openIndex === idx ? '#FFFFFF' : colors.primary }}
                  />
                </div>
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${
                openIndex === idx ? 'max-h-96' : 'max-h-0'
              }`}>
                <div className="px-6 pb-5 leading-relaxed" style={{ color: faqCardMutedColor }}>
                  <EditableText
                    value={item.answer}
                    onChange={(val) => updateItem(idx, 'answer', val)}
                    placeholder="Respuesta..."
                  />
                </div>
              </div>
            </div>
          )})}
        </div>
        
        <button
          onClick={addItem}
          className="mt-8 w-full py-4 border-2 border-dashed rounded-2xl transition-all flex items-center justify-center gap-2 hover:scale-[1.01]"
          style={{ 
            borderColor: `${colors.primary}40`,
            color: isDark ? colors.textMuted : '#64748b',
            backgroundColor: 'transparent'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.color = colors.primary;
            e.currentTarget.style.backgroundColor = `${colors.primary}08`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = `${colors.primary}40`;
            e.currentTarget.style.color = isDark ? colors.textMuted : '#64748b';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Plus className="w-5 h-5" />
          Añadir pregunta
        </button>
      </div>
    </div>
  );
};

// ==================== BLOCK RENDERER ====================
const BlockRenderer = ({ block, brandColors, colorScheme, expertPhoto, onUpdateContent, onUpdateStyles, briefData, api, viewMode = 'desktop' }) => {
  switch (block.type) {
    case 'hero':
      return <HeroBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} expertPhoto={expertPhoto} onUpdateContent={onUpdateContent} viewMode={viewMode} />;
    case 'benefits':
      return <BenefitsBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} briefData={briefData} api={api} viewMode={viewMode} />;
    case 'features':
      return <FeaturesBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} viewMode={viewMode} />;
    case 'cta':
      return <CTABlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} viewMode={viewMode} />;
    case 'testimonials':
      return <TestimonialsBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} viewMode={viewMode} />;
    case 'video':
      return <VideoBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} viewMode={viewMode} />;
    case 'faq':
      return <FAQBlockRenderer block={block} brandColors={brandColors} colorScheme={colorScheme} onUpdateContent={onUpdateContent} briefData={briefData} api={api} viewMode={viewMode} />;
    case 'live_room':
      // Special block for Live Room with dynamic states
      return (
        <div className="py-12 px-6">
          <LiveRoomStateIndicator 
            briefData={briefData}
            brandColors={brandColors}
            salaUrl={briefData?.sala_directo_1_url || block.content?.salaUrl}
            replayUrl={briefData?.replay_directo_1_url || block.content?.replayUrl}
          />
        </div>
      );
    default:
      return (
        <div className="py-12 px-6 bg-gray-100 text-center">
          <p className="text-gray-500">Bloque no reconocido: {block.type}</p>
        </div>
      );
  }
};

// ==================== BLOCK WRAPPER ====================
const BlockWrapper = ({ 
  block, 
  index, 
  total,
  brandColors,
  colorScheme,
  expertPhoto, 
  isSelected, 
  onSelect, 
  onUpdateContent, 
  onUpdateStyles,
  onMoveUp,
  onMoveDown,
  onDelete,
  briefData,
  api,
  viewMode = 'desktop'
}) => {
  const [showStyleEditor, setShowStyleEditor] = useState(false);
  const template = BLOCK_TEMPLATES[block.type];
  const IconComponent = template?.icon || Layout;

  return (
    <div 
      className={`relative group transition-all ${
        isSelected ? 'ring-2 ring-brand ring-offset-2 ring-offset-app-bg' : ''
      }`}
      onClick={() => onSelect(block.id)}
    >
      {/* Block Type Label */}
      <div className={`absolute -top-3 left-4 z-20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
        isSelected ? 'bg-brand-500 text-white' : 'bg-surface border border-border-subtle text-text-secondary'
      }`}>
        <IconComponent className="w-3 h-3" />
        {template?.label || block.type}
      </div>

      {/* Toolbar */}
      <div className={`absolute -top-3 right-4 z-20 flex gap-1 transition-opacity ${
        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          disabled={index === 0}
          className="p-1.5 bg-surface border border-border-subtle rounded-lg hover:bg-brand-500 hover:text-white hover:border-brand-500 disabled:opacity-30 disabled:hover:bg-surface disabled:hover:text-text-secondary"
        >
          <ArrowUp className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          disabled={index === total - 1}
          className="p-1.5 bg-surface border border-border-subtle rounded-lg hover:bg-brand-500 hover:text-white hover:border-brand-500 disabled:opacity-30"
        >
          <ArrowDown className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowStyleEditor(!showStyleEditor); }}
          className={`p-1.5 border rounded-lg ${
            showStyleEditor ? 'bg-brand-500 text-white border-brand' : 'bg-surface border-border-subtle hover:bg-brand-500 hover:text-white hover:border-brand'
          }`}
        >
          <Settings2 className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1.5 bg-surface border border-border-subtle rounded-lg hover:bg-red-500 hover:text-white hover:border-red-500"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Style Editor Panel */}
      {showStyleEditor && (
        <StyleEditorPanel
          block={block}
          brandColors={brandColors}
          onUpdateStyles={(styles) => onUpdateStyles(block.id, styles)}
          onClose={() => setShowStyleEditor(false)}
        />
      )}

      {/* Block Content */}
      <BlockRenderer
        block={block}
        brandColors={brandColors}
        colorScheme={colorScheme}
        expertPhoto={expertPhoto}
        onUpdateContent={(content) => onUpdateContent(block.id, content)}
        onUpdateStyles={(styles) => onUpdateStyles(block.id, styles)}
        briefData={briefData}
        api={api}
        viewMode={viewMode}
      />
    </div>
  );
};

// ==================== ADD BLOCK BUTTON ====================
const AddBlockButton = ({ onAddBlock }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative py-4">
      <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-border-subtle" />
      <div className="relative flex justify-center">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
            isOpen 
              ? 'bg-brand-500 text-white border-brand' 
              : 'bg-surface border-border-subtle text-text-secondary hover:border-brand-500 hover:text-brand'
          }`}
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Añadir Bloque</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <>
          {/* Overlay to catch clicks outside */}
          <div 
            className="fixed inset-0 z-20" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            ref={menuRef}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-surface border border-border-subtle rounded-xl shadow-2xl p-3 z-30 min-w-80"
          >
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(BLOCK_TEMPLATES).map(([key, template]) => {
                const IconComponent = template.icon;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      onAddBlock(key);
                      setIsOpen(false);
                    }}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand/10 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-brand-500" />
                    </div>
                    <span className="font-medium text-text-main text-sm">{template.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== MAIN LANDING CANVAS COMPONENT ====================
const LandingCanvas = ({ 
  landingId,
  funnelId,
  initialBlocks = [],
  briefData = {},
  onSave,
  readOnly = false
}) => {
  const { api } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [viewMode, setViewMode] = useState('desktop'); // desktop, mobile
  const [isPreview, setIsPreview] = useState(readOnly); // Start in preview if readOnly
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // ====== UNDO HISTORY ======
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistorySize = 20;
  
  // ====== CUSTOM COLORS ======
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDesignPanel, setShowDesignPanel] = useState(false);
  const [selectedColorScheme, setSelectedColorScheme] = useState('midnight');
  const [selectedTypography, setSelectedTypography] = useState('modern');
  const [customColors, setCustomColors] = useState({
    primary: null,
    secondary: null
  });
  const colorPickerRef = useRef(null);
  const designPanelRef = useRef(null);

  // Helper to darken a hex color
  const darkenHex = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return `#${(1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1).toUpperCase()}`;
  };

  // Helper to lighten a hex color
  const lightenHex = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
    const B = Math.min(255, (num & 0x0000FF) + amt);
    return `#${(1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1).toUpperCase()}`;
  };

  // Brand colors from brief (with custom override or from color scheme)
  const baseColorScheme = COLOR_SCHEMES[selectedColorScheme] || COLOR_SCHEMES.midnight;
  
  // PRIORITY ORDER for brand colors:
  // 1. Custom colors (user explicitly set)
  // 2. Brief colors from web analysis (should be preserved)
  // 3. Color scheme defaults
  const briefPrimaryColor = briefData?.primaryColor || briefData?.brandAssets?.primaryColor;
  const briefSecondaryColor = briefData?.secondaryColor || briefData?.brandAssets?.secondaryColor;
  
  const brandColors = {
    primary: customColors.primary || briefPrimaryColor || baseColorScheme.primary || '#6366F1',
    secondary: customColors.secondary || briefSecondaryColor || baseColorScheme.secondary || '#00D68F'
  };

  // Generate dynamic color scheme if brief has custom colors
  // This ensures the ENTIRE landing uses the brand colors, not just accents
  const colorScheme = (briefPrimaryColor && !customColors.primary) ? {
    ...baseColorScheme,
    id: 'brand-custom',
    name: 'Colores de Marca',
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    accent: briefData?.accentColor || briefData?.brandAssets?.accentColor || brandColors.secondary,
    // Generate matching gradients from brand colors
    gradient: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
    heroGradient: `linear-gradient(180deg, ${darkenHex(brandColors.primary, 85)} 0%, ${darkenHex(brandColors.primary, 75)} 50%, ${darkenHex(brandColors.primary, 85)} 100%)`,
    // Keep dark background but tint with brand color
    background: darkenHex(brandColors.primary, 90),
    surface: darkenHex(brandColors.primary, 80),
  } : baseColorScheme;

  // Expert photo
  const expertPhoto = briefData?.brandAssets?.expertPhoto || briefData?.expertPhoto;

  // Helper to generate default contextualized blocks
  const createDefaultBlocks = useCallback(() => {
    const productName = briefData?.productName || briefData?.product_name || 'nuestro producto';
    const targetAudience = briefData?.targetAudience || briefData?.target_audience || 'profesionales';
    const mainPromise = briefData?.mainPromise || briefData?.main_promise || briefData?.promised_result || 'conseguir resultados';
    const painPoints = briefData?.painPoints || briefData?.pain_points || '';
    const solutionOverview = briefData?.solutionOverview || briefData?.solution_overview || '';
    
    // Parse pain points
    const painPointsArray = typeof painPoints === 'string' 
      ? painPoints.split(/[;\n]/).filter(p => p.trim()).slice(0, 3)
      : [];

    return [
      {
        id: `block-${Date.now()}-1`,
        type: 'hero',
        content: {
          preheadline: `Para ${targetAudience} que quieren ${mainPromise}`,
          headline: briefData?.generated?.headline || briefData?.selectedTitle || `Descubre ${productName}`,
          subheadline: briefData?.generated?.subheadline || mainPromise,
          cta: 'QUIERO ACCEDER AHORA',
          ctaSubtext: '100% garantizado o te devolvemos tu dinero'
        },
        styles: BLOCK_TEMPLATES.hero.defaultStyles
      },
      {
        id: `block-${Date.now()}-2`,
        type: 'benefits',
        content: {
          title: `Lo que conseguirás con ${productName}`,
          items: painPointsArray.length >= 3 ? [
            { icon: 'check', title: 'Solución al problema #1', description: `Ya no: ${painPointsArray[0]}` },
            { icon: 'check', title: 'Solución al problema #2', description: `Olvídate de: ${painPointsArray[1]}` },
            { icon: 'check', title: 'Solución al problema #3', description: `Nunca más: ${painPointsArray[2]}` }
          ] : [
            { icon: 'check', title: 'Resultados garantizados', description: mainPromise || 'Conseguirás los resultados que buscas' },
            { icon: 'check', title: 'Sistema probado', description: solutionOverview || 'Un método paso a paso diseñado para ti' },
            { icon: 'check', title: 'Soporte incluido', description: 'No estarás solo en el proceso' }
          ]
        },
        styles: BLOCK_TEMPLATES.benefits.defaultStyles
      },
      {
        id: `block-${Date.now()}-3`,
        type: 'cta',
        content: {
          headline: `¿Listo para ${mainPromise}?`,
          subheadline: `Únete a los ${targetAudience} que ya están consiguiendo resultados`,
          cta: 'COMENZAR AHORA',
          urgency: 'Plazas limitadas - Accede hoy'
        },
        styles: BLOCK_TEMPLATES.cta.defaultStyles
      }
    ];
  }, [briefData]);

  // Initialize blocks from initialBlocks or create defaults with brief context
  useEffect(() => {
    if (initialBlocks && initialBlocks.length > 0) {
      setBlocks(initialBlocks);
      setHasChanges(false);
    } else {
      setBlocks(createDefaultBlocks());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingId]); // Re-run when landingId changes (initialBlocks is initial value only)

  // Listen for messages from preview window
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'PREVIEW_SAVE' && event.data?.landingId === landingId) {
        setBlocks(event.data.blocks);
        setHasChanges(true);
        toast.success('Cambios recibidos desde el editor visual');
      }
    };

    const handleStorageChange = (e) => {
      if (e.key === `preview_saved_${landingId}`) {
        try {
          const data = JSON.parse(e.newValue);
          setBlocks(data.blocks);
          setHasChanges(true);
          toast.success('Cambios sincronizados desde vista previa');
        } catch (err) {
          
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [landingId]);

  // Open external preview editor
  const openExternalPreview = () => {
    // Save current state to localStorage for the preview to pick up
    const previewData = {
      blocks,
      briefData: {
        ...briefData,
        primaryColor: brandColors.primary,
        secondaryColor: brandColors.secondary,
        expertPhoto
      }
    };
    localStorage.setItem(`preview_${landingId}`, JSON.stringify(previewData));
    
    // Open preview in new tab
    const previewUrl = `/preview/${landingId}`;
    window.open(previewUrl, '_blank');
    toast.info('Editor visual abierto en nueva pestaña');
  };

  // Handle save
  const handleSave = async () => {
    if (readOnly) return; // Don't save in readOnly mode
    
    setSaving(true);
    try {
      if (onSave) {
        await onSave(blocks);
      }
      
      // Also save to backend
      if (funnelId && landingId) {
        await api.put(`/funnels/${funnelId}/landings/${landingId}`, {
          blocks: blocks
        });
      }
      
      setHasChanges(false);
      toast.success('Landing guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ====== UNDO FUNCTIONALITY ======
  const pushToHistory = useCallback((newBlocks) => {
    setHistory(prev => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      newHistory.push(JSON.stringify(newBlocks));
      // Limit history size
      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, maxHistorySize - 1));
  }, [historyIndex, maxHistorySize]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const previousState = JSON.parse(history[historyIndex - 1]);
      setBlocks(previousState);
      setHistoryIndex(prev => prev - 1);
      setHasChanges(true);
      toast.info('Cambio deshecho');
    }
  }, [history, historyIndex]);

  const canUndo = historyIndex > 0;

  // ====== COLOR PICKER HANDLERS ======
  const handleColorChange = (colorType, value) => {
    setCustomColors(prev => ({
      ...prev,
      [colorType]: value
    }));
    setHasChanges(true);
    toast.success(`Color ${colorType === 'primary' ? 'primario' : 'secundario'} actualizado`);
  };

  const resetColors = () => {
    setCustomColors({ primary: null, secondary: null });
    setHasChanges(true);
    toast.info('Colores restaurados a los originales');
    setShowColorPicker(false);
  };

  // Close color picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ==================== GENERATE CONTEXTUALIZED BLOCK CONTENT ====================
  // Uses briefData to create relevant content instead of generic placeholders
  const generateContextualizedContent = useCallback((type) => {
    const productName = briefData?.productName || briefData?.product_name || 'nuestro producto';
    const targetAudience = briefData?.targetAudience || briefData?.target_audience || 'profesionales';
    const mainPromise = briefData?.mainPromise || briefData?.main_promise || briefData?.promised_result || 'conseguir resultados';
    const painPoints = briefData?.painPoints || briefData?.pain_points || '';
    const offerDetails = briefData?.offerDetails || briefData?.offer_details || '';
    const whatYouSell = briefData?.whatYouSell || briefData?.what_you_sell || '';
    const solutionOverview = briefData?.solutionOverview || briefData?.solution_overview || '';
    const mainObjections = briefData?.mainObjections || briefData?.main_objections || '';
    const price = briefData?.price || '';
    
    // Parse pain points into array if string
    const painPointsArray = typeof painPoints === 'string' 
      ? painPoints.split(/[;\n]/).filter(p => p.trim()).slice(0, 3)
      : [];
    
    // Parse offer details into array if string  
    const offerArray = typeof offerDetails === 'string'
      ? offerDetails.split(/[;\n]/).filter(p => p.trim()).slice(0, 3)
      : [];
    
    // Parse objections into array for FAQ
    const objectionsArray = typeof mainObjections === 'string'
      ? mainObjections.split(/[;\n]/).filter(p => p.trim()).slice(0, 5)
      : [];

    switch (type) {
      case 'hero':
        return {
          preheadline: `Para ${targetAudience} que quieren ${mainPromise}`,
          headline: briefData?.generated?.headline || briefData?.selectedTitle || `Descubre ${productName}`,
          subheadline: briefData?.generated?.subheadline || mainPromise || whatYouSell,
          cta: `QUIERO ${mainPromise.toUpperCase().slice(0, 30)}`,
          ctaSubtext: '100% garantizado o te devolvemos tu dinero'
        };
        
      case 'benefits':
        return {
          title: `Lo que conseguirás con ${productName}`,
          items: painPointsArray.length >= 3 ? [
            { icon: 'check', title: 'Solución al problema #1', description: `Ya no tendrás que preocuparte por: ${painPointsArray[0]}` },
            { icon: 'check', title: 'Solución al problema #2', description: `Olvídate de: ${painPointsArray[1]}` },
            { icon: 'check', title: 'Solución al problema #3', description: `Nunca más: ${painPointsArray[2]}` }
          ] : [
            { icon: 'check', title: 'Resultados garantizados', description: mainPromise || 'Conseguirás los resultados que buscas' },
            { icon: 'check', title: 'Sistema probado', description: solutionOverview || 'Un método paso a paso diseñado para ti' },
            { icon: 'check', title: 'Soporte incluido', description: 'No estarás solo en el proceso' }
          ]
        };
        
      case 'testimonials':
        return {
          title: `Lo que dicen sobre ${productName}`,
          items: [
            { name: 'Cliente Satisfecho', role: targetAudience, text: `${productName} me ayudó a ${mainPromise}. Los resultados superaron mis expectativas.`, avatar: null },
            { name: 'Usuario Verificado', role: 'Profesional', text: `Recomiendo ${productName} a cualquiera que busque resultados reales.`, avatar: null }
          ]
        };
        
      case 'features':
        return {
          title: `¿Qué incluye ${productName}?`,
          items: offerArray.length >= 3 ? [
            { icon: 'zap', title: 'Incluido', value: offerArray[0] },
            { icon: 'zap', title: 'Incluido', value: offerArray[1] },
            { icon: 'zap', title: 'Incluido', value: offerArray[2] }
          ] : [
            { icon: 'zap', title: 'Acceso completo', value: whatYouSell || 'Todo el contenido del programa' },
            { icon: 'zap', title: 'Soporte', value: 'Acompañamiento durante el proceso' },
            { icon: 'zap', title: 'Garantía', value: 'Satisfacción garantizada' }
          ]
        };
        
      case 'cta':
        return {
          headline: `¿Listo para **${mainPromise}**?`,
          subheadline: `Únete a los **${targetAudience}** que ya están consiguiendo resultados con **${productName}**`,
          cta: `QUIERO ${mainPromise.toUpperCase().slice(0, 25)}`,
          urgency: price ? `Inversión: ${price} - Accede hoy` : 'Plazas limitadas - Accede hoy'
        };
        
      case 'video':
        return {
          title: `Descubre cómo ${productName} puede ayudarte`,
          subtitle: mainPromise,
          videoUrl: '',
          placeholder: 'Añade tu video explicativo aquí'
        };
      
      case 'faq':
        // Generate FAQ based on objections and offer details
        const faqItems = [];
        
        // Add questions based on main objections
        if (objectionsArray.length > 0) {
          objectionsArray.forEach((objection, idx) => {
            if (idx < 3) {
              faqItems.push({
                question: `¿${objection.replace(/^[¿?]/, '').replace(/[?]$/, '')}?`,
                answer: solutionOverview || `${productName} está diseñado específicamente para resolver esta preocupación. Nuestro sistema probado garantiza resultados.`
              });
            }
          });
        }
        
        // Add standard questions if we don't have enough from objections
        if (faqItems.length < 4) {
          const standardQuestions = [
            { 
              question: `¿Para quién es ${productName}?`, 
              answer: `${productName} está diseñado para **${targetAudience}** que quieren ${mainPromise}.` 
            },
            { 
              question: `¿Qué incluye exactamente?`, 
              answer: offerDetails || whatYouSell || `Acceso completo a ${productName} con todo lo necesario para conseguir resultados.`
            },
            { 
              question: `¿Qué garantía tengo?`, 
              answer: `Garantía de satisfacción incluida. Si no consigues ${mainPromise}, te devolvemos tu inversión.`
            },
            { 
              question: price ? `¿Por qué cuesta ${price}?` : `¿Cuál es la inversión?`, 
              answer: `${productName} es una inversión en ${mainPromise}. El valor que recibes supera con creces la inversión.`
            },
            {
              question: `¿Cuánto tiempo tendré acceso?`,
              answer: `Acceso completo desde el primer día. Todo el contenido disponible para ti cuando lo necesites.`
            }
          ];
          
          standardQuestions.forEach(q => {
            if (faqItems.length < 5) {
              faqItems.push(q);
            }
          });
        }
        
        return {
          title: `Preguntas Frecuentes sobre ${productName}`,
          items: faqItems
        };
        
      default:
        return BLOCK_TEMPLATES[type]?.defaultContent || {};
    }
  }, [briefData]);

  // Block operations - Now with contextualized content
  const addBlock = (type, index = blocks.length) => {
    const template = BLOCK_TEMPLATES[type];
    const contextualizedContent = generateContextualizedContent(type);
    
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: { ...template.defaultContent, ...contextualizedContent },
      styles: { ...template.defaultStyles }
    };
    
    const newBlocks = [...blocks];
    newBlocks.splice(index, 0, newBlock);
    pushToHistory(newBlocks);
    setBlocks(newBlocks);
    setSelectedBlockId(newBlock.id);
    setHasChanges(true);
  };

  const updateBlockContent = (blockId, content) => {
    const newBlocks = blocks.map(b => b.id === blockId ? { ...b, content } : b);
    pushToHistory(newBlocks);
    setBlocks(newBlocks);
    setHasChanges(true);
  };

  const updateBlockStyles = (blockId, styles) => {
    const newBlocks = blocks.map(b => b.id === blockId ? { ...b, styles: { ...b.styles, ...styles } } : b);
    pushToHistory(newBlocks);
    setBlocks(newBlocks);
    setHasChanges(true);
  };

  const moveBlock = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    pushToHistory(newBlocks);
    setBlocks(newBlocks);
    setHasChanges(true);
  };

  const deleteBlock = (blockId) => {
    if (blocks.length <= 1) {
      toast.error('Debe haber al menos un bloque');
      return;
    }
    const newBlocks = blocks.filter(b => b.id !== blockId);
    pushToHistory(newBlocks);
    setBlocks(newBlocks);
    setSelectedBlockId(null);
    setHasChanges(true);
  };

  return (
    <div className="h-full flex flex-col" data-testid="landing-canvas">
      {/* Toolbar */}
      <div className="bg-surface border-b border-border-subtle px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex bg-app-bg rounded-lg p-1 border border-border-subtle">
            <button
              onClick={() => setViewMode('desktop')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'desktop' ? 'bg-brand-500 text-white' : 'text-text-muted hover:text-text-main'
              }`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`p-2 rounded-md transition-all ${
                viewMode === 'mobile' ? 'bg-brand-500 text-white' : 'text-text-muted hover:text-text-main'
              }`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          {/* Block Count */}
          <span className="text-sm text-text-muted">
            {blocks.length} bloques
          </span>

          {hasChanges && (
            <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full">
              Sin guardar
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Undo Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={!canUndo}
            className="border-border-subtle"
            title="Deshacer (Ctrl+Z)"
          >
            <Undo2 className="w-4 h-4" />
          </Button>

          {/* Design System Button */}
          <div className="relative" ref={designPanelRef}>
            <button
              onClick={() => setShowDesignPanel(!showDesignPanel)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                showDesignPanel 
                  ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-500' 
                  : 'bg-app-bg border-border-subtle hover:border-indigo-500/50'
              }`}
              title="Plantillas y diseño premium"
            >
              <LayoutTemplate className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-medium text-text-main">Diseño</span>
              <Sparkles className="w-3 h-3 text-amber-400" />
            </button>

            {/* Design Panel Dropdown */}
            {showDesignPanel && (
              <div className="absolute top-full right-0 mt-2 z-50">
                <DesignSettingsPanel
                  colorScheme={selectedColorScheme}
                  typography={selectedTypography}
                  onColorSchemeChange={(scheme) => {
                    setSelectedColorScheme(scheme);
                    const colors = COLOR_SCHEMES[scheme];
                    if (colors) {
                      setCustomColors({ primary: colors.primary, secondary: colors.secondary });
                    }
                  }}
                  onTypographyChange={setSelectedTypography}
                  onClose={() => setShowDesignPanel(false)}
                />
              </div>
            )}
          </div>

          {/* Brand Colors Picker */}
          <div className="relative" ref={colorPickerRef}>
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                showColorPicker 
                  ? 'bg-brand/10 border-brand' 
                  : 'bg-app-bg border-border-subtle hover:border-brand/50'
              }`}
              title="Ajustar colores"
            >
              <Palette className="w-4 h-4 text-text-muted" />
              <div className="flex gap-1">
                <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: brandColors.primary }} />

                <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: brandColors.secondary }} />
              </div>
              <ChevronDown className={`w-3 h-3 text-text-muted transition-transform ${showColorPicker ? 'rotate-180' : ''}`} />
            </button>

            {/* Color Picker Dropdown */}
            {showColorPicker && (
              <div className="absolute top-full right-0 mt-2 p-4 bg-surface border border-border-subtle rounded-xl shadow-2xl z-50 min-w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-text-main">Colores de marca</span>
                  <button
                    onClick={resetColors}
                    className="text-xs text-text-muted hover:text-brand-500 transition-colors"
                  >
                    Restaurar
                  </button>
                </div>
                
                {/* Primary Color */}
                <div className="mb-4">
                  <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Color Primario</label>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={brandColors.primary}
                        onChange={(e) => handleColorChange('primary', e.target.value)}
                        className="w-10 h-10 rounded-lg cursor-pointer border-2 border-border-subtle"
                        style={{ backgroundColor: brandColors.primary }}
                      />
                    </div>
                    <input
                      type="text"
                      value={brandColors.primary}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="flex-1 bg-app-bg border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-main font-mono uppercase"
                      placeholder="#6366F1"
                    />
                  </div>
                </div>
                
                {/* Secondary Color */}
                <div className="mb-4">
                  <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Color Secundario</label>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={brandColors.secondary}
                        onChange={(e) => handleColorChange('secondary', e.target.value)}
                        className="w-10 h-10 rounded-lg cursor-pointer border-2 border-border-subtle"
                        style={{ backgroundColor: brandColors.secondary }}
                      />
                    </div>
                    <input
                      type="text"
                      value={brandColors.secondary}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="flex-1 bg-app-bg border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-main font-mono uppercase"
                      placeholder="#00D68F"
                    />
                  </div>
                </div>

                {/* Preset Colors */}
                <div>
                  <label className="text-xs text-text-muted uppercase tracking-wider mb-2 block">Presets rápidos</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { primary: '#6366F1', secondary: '#00D68F' }, // Default
                      { primary: '#8B5CF6', secondary: '#F59E0B' }, // Purple/Amber
                      { primary: '#EC4899', secondary: '#14B8A6' }, // Pink/Teal
                      { primary: '#EF4444', secondary: '#3B82F6' }, // Red/Blue
                      { primary: '#10B981', secondary: '#F97316' }, // Emerald/Orange
                    ].map((preset) => (
                      <button
                        key={`${preset.primary}-${preset.secondary}`}
                        onClick={() => {
                          handleColorChange('primary', preset.primary);
                          handleColorChange('secondary', preset.secondary);
                        }}
                        className="flex flex-col gap-0.5 p-1 rounded-lg hover:bg-white/10 transition-colors"
                        title={`Preset ${preset.primary}`}
                      >
                        <div className="w-full h-3 rounded-t" style={{ backgroundColor: preset.primary }} />
                        <div className="w-full h-3 rounded-b" style={{ backgroundColor: preset.secondary }} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Internal Preview Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPreview(!isPreview)}
            className={`border-border-subtle ${isPreview ? 'bg-brand-500 text-white' : ''}`}
            disabled={readOnly} // Disable toggle in readOnly mode
          >
            {isPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {isPreview ? 'Editar' : 'Preview'}
          </Button>

          {/* External Preview Button - Opens in new tab */}
          <Button
            variant="outline"
            size="sm"
            onClick={openExternalPreview}
            className="border-border-subtle bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-none hover:from-indigo-600 hover:to-purple-600"
            data-testid="open-external-preview"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Vista Previa
          </Button>

          {/* Save Button - Hidden in readOnly mode */}
          {!readOnly && (
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-brand-500 hover:bg-brand-hover text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-auto bg-app-bg p-6">
        <div 
          className={`mx-auto transition-all duration-300 ${
            viewMode === 'mobile' ? 'max-w-sm' : 'max-w-5xl'
          }`}
        >
          {/* Meta Pixel Tracker - only renders if pixelId is present */}
          {briefData?.meta_pixel_id && (
            <MetaPixelTracker
              pixelId={briefData.meta_pixel_id}
              landingSlug={landingId}
              briefData={briefData}
            />
          )}
          
          {/* Landing Preview */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            {blocks.map((block, index) => (
              <div key={block.id}>
                {!isPreview && index === 0 && (
                  <AddBlockButton onAddBlock={(type) => addBlock(type, 0)} />
                )}
                
                {isPreview ? (
                  <BlockRenderer
                    block={block}
                    brandColors={brandColors}
                    colorScheme={colorScheme}
                    expertPhoto={expertPhoto}
                    onUpdateContent={() => {}}
                    onUpdateStyles={() => {}}
                    viewMode={viewMode}
                  />
                ) : (
                  <BlockWrapper
                    block={block}
                    index={index}
                    total={blocks.length}
                    brandColors={brandColors}
                    colorScheme={colorScheme}
                    expertPhoto={expertPhoto}
                    isSelected={selectedBlockId === block.id}
                    onSelect={setSelectedBlockId}
                    onUpdateContent={updateBlockContent}
                    onUpdateStyles={updateBlockStyles}
                    onMoveUp={() => moveBlock(index, 'up')}
                    onMoveDown={() => moveBlock(index, 'down')}
                    onDelete={() => deleteBlock(block.id)}
                    briefData={briefData}
                    api={api}
                    viewMode={viewMode}
                  />
                )}

                {!isPreview && (
                  <AddBlockButton onAddBlock={(type) => addBlock(type, index + 1)} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingCanvas;
